import type { OrganizationDraftYearsResponse } from '@on3/api';
import { getTalentRatioColor } from '@on3/ui-lib/utils/getTalentRatioColor';
import { TextLink } from '@public/components/UI/Link/TextLink';
import { Text } from '@public/components/UI/Typography/Text';
import clsx from 'clsx';
import { useRouter } from 'next/router';

import styles from './DraftHistoryItem.module.scss';

interface IDraftHistoryItemProps extends OrganizationDraftYearsResponse {
  index: number;
}
export const DraftHistoryItem = ({
  year,
  fiveStars,
  fourStars,
  threeStars,
  total,
  blueChipPercent: blueChip,
  talentRatio: talent,
  index,
}: IDraftHistoryItemProps) => {
  const router = useRouter();
  const { 'team-key': teamKey } = router.query;
  const talentRatio = `${Math.round(talent ?? 0)}%`;
  const blueChipPercent = blueChip ? `${blueChip.toFixed(1)}%` : '0%';
  const talentRatioColor = getTalentRatioColor(talent ?? 0);
  const league = router.asPath.includes('nfl') ? 'nfl' : 'nba';

  return (
    <div
      className={clsx(styles.container, { [styles.addMargin]: index === 0 })}
    >
      <TextLink
        className={styles.year}
        href={`/${league}/draft/${year}/draft-history/top-draft-picks/?team-key=${teamKey}`}
        underline="none"
        variant="subtitle2"
      >
        {year}
      </TextLink>
      <div className={styles.starsWrapper}>
        <div className={styles.starsContainer}>
          <Text className={styles.starsLabel} variant="subtitle1">
            <span>
              <span className={styles.addDash}>5</span>
              stars
            </span>
          </Text>
          <Text className={styles.value} component="span">
            {fiveStars}
          </Text>
        </div>
        <div className={styles.starsContainer}>
          <Text className={styles.starsLabel} variant="subtitle1">
            <span>
              <span className={styles.addDash}>4</span>
              stars
            </span>
          </Text>
          <Text className={styles.value} component="span">
            {fourStars}
          </Text>
        </div>
        <div className={styles.starsContainer}>
          <Text className={styles.starsLabel} variant="subtitle1">
            <span>
              <span className={styles.addDash}>3</span>
              stars
            </span>
          </Text>
          <Text className={styles.value} component="span">
            {threeStars}
          </Text>
        </div>
        <div className={styles.totalContainer}>
          <Text className={styles.totalLabel} variant="subtitle1">
            Total
          </Text>
          <Text className={styles.value} component="span">
            {total}
          </Text>
        </div>
      </div>
      <Text className={styles.perCapitaLabel} variant="overline">
        Per capita
      </Text>
      <div className={styles.percentsWrapper}>
        <div className={clsx(styles.percentContainer, styles.blueChip)}>
          <Text className={styles.percentLabel} variant="subtitle1">
            <span className={styles.draftedTitle}>drafted</span>{' '}
            <span>blue chip share</span>
          </Text>
          <Text className={styles.value} component="span">
            {blueChipPercent}
          </Text>
        </div>
        <div
          className={clsx(
            styles.percentContainer,
            styles.talent,
            styles[talentRatioColor],
          )}
        >
          <Text className={styles.percentLabel} variant="subtitle1">
            <span className={styles.draftedTitle}>drafted</span>{' '}
            <span>
              talent ratio <span aria-label="Percentage">%</span>
            </span>
          </Text>
          <Text className={styles.value} component="span">
            {talentRatio}
          </Text>
        </div>
      </div>
    </div>
  );
};
